import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import {
  REQUEST_STATUSES, TOW_STATUSES, STATUS_LABELS, STATUS_COLORS, TOW_TYPES,
} from "@/lib/constants";
import Shell from "@/components/Shell";
import MapView from "@/components/MapView";
import StatusStepper from "@/components/StatusStepper";
import { StarDisplay, StarInput } from "@/components/StarRating";
import ChatBox from "@/components/ChatBox";
import { AttachmentsGallery } from "@/components/AttachmentsGallery";
import { WorkPhotos } from "@/components/WorkPhotos";
import PriceItems from "@/components/PriceItems";
import { PayButtons } from "@/components/PayButtons";
import { RequestInvoices } from "@/components/RequestInvoices";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ArrowRight, Wrench, Truck, MapPin, Clock, ShieldCheck, CheckCircle2,
  Phone, Star, Zap, Car, BadgeCheck, CreditCard, ExternalLink, Info, Printer,
} from "lucide-react";
import { toast } from "sonner";

export default function RequestDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [req, setReq] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [tow, setTow] = useState(null);
  const [towOffers, setTowOffers] = useState([]);
  const [towOpen, setTowOpen] = useState(false);
  const [rateOpen, setRateOpen] = useState(false);

  const [tf, setTf] = useState({
    to_location: "", tow_type: "عادية", pickup_time: "الآن", notes: "",
  });
  const [rate, setRate] = useState({ service_quality: 5, response_speed: 5, price_rating: 5, comment: "" });

  const load = async () => {
    const { data } = await api.get(`/requests/${id}`);
    setReq(data);
    const q = await api.get(`/requests/${id}/quotes`);
    setQuotes(q.data);
    const tows = await api.get("/tow-requests");
    const linked = tows.data.find((t) => t.request_id === id);
    setTow(linked || null);
    if (linked) {
      const o = await api.get(`/tow-requests/${linked.id}/offers`);
      setTowOffers(o.data);
    }
  };
  useEffect(() => { load(); const t = setInterval(load, 7000); return () => clearInterval(t); }, [id]);

  if (!req) return <Shell title="جارٍ التحميل..."><div /></Shell>;

  const acceptQuote = async (qid) => {
    try {
      await api.post(`/quotes/${qid}/accept`);
      toast.success("تم قبول العرض! تم كشف بيانات الورشة وإنشاء أمر العمل");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const printReceipt = async () => {
    const r = req.receipt;
    if (!r) return;
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write("<p dir='rtl' style='font-family:Arial;padding:32px'>جارٍ تجهيز السند...</p>");
    const toDataUrl = (blob) => new Promise((res) => { const fr = new FileReader(); fr.onload = () => res(fr.result); fr.readAsDataURL(blob); });
    let photosHtml = "";
    try {
      const { data: wp } = await api.get(`/work-photos/${id}`);
      const section = async (list, title) => {
        const imgs = list.filter((a) => a.content_type.startsWith("image/") && a.content_type !== "image/heic");
        if (!imgs.length) return "";
        const urls = await Promise.all(imgs.map((a) => api.get(`/chat-files/file/${a.file_id}`, { responseType: "blob" }).then((x) => toDataUrl(x.data)).catch(() => "")));
        return `<h3>${title}</h3><div class="ph">${urls.filter(Boolean).map((u) => `<img src="${u}"/>`).join("")}</div>`;
      };
      photosHtml = (await section(wp.before, "صور الاستلام")) + (await section(wp.after, "صور بعد الإصلاح"));
    } catch { photosHtml = ""; }
    const extras = (r.extras || []).map((e) =>
      `<tr><td>${e.type === "parts" ? "قطع غيار" : "عمل إضافي"}: ${e.description}</td><td>${e.amount} ر.س</td></tr>`).join("");
    w.document.open();
    w.document.write(`<html dir="rtl" lang="ar"><head><meta charset="utf-8"><title>سند استلام ${r.receipt_no}</title>
      <style>body{font-family:Arial,'Segoe UI',sans-serif;padding:32px;color:#0f172a}
      h1{color:#2563eb;margin:0;font-size:22px} .h{display:flex;justify-content:space-between;align-items:center;border-bottom:3px solid #2563eb;padding-bottom:14px;margin-bottom:8px}
      table{width:100%;border-collapse:collapse;margin-top:16px} td{padding:10px;border-bottom:1px solid #e2e8f0}
      td:last-child{text-align:left;font-weight:600} .tot td{font-size:20px;font-weight:800;color:#2563eb;border-top:2px solid #2563eb}
      h3{margin:22px 0 8px;font-size:15px} .ph{display:flex;flex-wrap:wrap;gap:8px} .ph img{width:150px;height:110px;object-fit:cover;border-radius:8px;border:1px solid #e2e8f0}
      .note{margin-top:24px;color:#64748b}</style></head>
      <body>
        <div class="h"><h1>اسناد كار — سند استلام مركبة</h1><div>رقم السند: <b>${r.receipt_no}</b></div></div>
        <table>
          <tr><td>المركبة</td><td>${r.vehicle} — ${r.plate}</td></tr>
          <tr><td>الصيانة المنفذة</td><td>${r.service}</td></tr>
          <tr><td>الورشة</td><td>${r.workshop_name}</td></tr>
          <tr><td>المبلغ الأساسي</td><td>${r.base_amount ?? r.amount} ر.س</td></tr>
          ${extras}
          <tr class="tot"><td>الإجمالي المدفوع</td><td>${r.amount} ر.س</td></tr>
          <tr><td>التاريخ</td><td>${new Date(r.date).toLocaleString("ar-SA")}</td></tr>
        </table>
        ${photosHtml}
        <p class="note">اعرض هذا السند لدى الورشة لاستلام مركبتك.</p>
      </body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 600);
  };

  const cancelReq = async () => {
    const reason = window.prompt("سبب الإلغاء (اختياري)") || "";
    try { await api.post(`/requests/${id}/cancel`, { reason }); toast.success("تم إلغاء الطلب"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const createTow = async (e) => {
    e.preventDefault();
    try {
      await api.post("/tow-requests", {
        request_id: id,
        from_location: req.location, from_lat: req.lat, from_lng: req.lng,
        to_location: tf.to_location, to_lat: (req.lat || 24.71) + 0.03, to_lng: (req.lng || 46.67) + 0.03,
        vehicle_type: `${req.vehicle?.make} ${req.vehicle?.model}`,
        drivable: req.drivable, tow_type: tf.tow_type, pickup_time: tf.pickup_time, notes: tf.notes,
      });
      toast.success("تم إرسال طلب السطحة للمزودين القريبين");
      setTowOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const acceptTowOffer = async (oid) => {
    try { await api.post(`/tow-offers/${oid}/accept`); toast.success("تم قبول عرض السحب"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const selectedQuote = quotes.find((q) => q.id === req.selected_quote_id);
  const canRate = ["delivered", "closed"].includes(req.status) && selectedQuote;

  const submitRate = async (e) => {
    e.preventDefault();
    try {
      await api.post("/ratings", {
        target_type: "workshop", target_id: selectedQuote.workshop_id, request_id: id, ...rate,
      });
      toast.success("شكراً لتقييمك!");
      setRateOpen(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const markers = [];
  if (req.lat) markers.push({ lat: req.lat, lng: req.lng, color: "#2563EB", emoji: "", label: "موقع المركبة" });
  const towAccepted = tow?.accepted_offer;
  if (tow && tow.driver_lat) markers.push({ lat: tow.driver_lat, lng: tow.driver_lng, color: "#F97316", label: "السطحة" });

  return (
    <Shell title="تفاصيل طلب الصيانة" subtitle={`${req.vehicle?.make} ${req.vehicle?.model} • ${req.vehicle?.plate}`} icon={Wrench}
      actions={<Button variant="outline" className="rounded-full gap-2" onClick={() => navigate("/app")} data-testid="back-btn"><ArrowRight className="w-4 h-4" /> رجوع</Button>}>
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left: info + stepper */}
        <div className="space-y-6">
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <Badge variant="outline" className={STATUS_COLORS[req.status]}>{STATUS_LABELS[req.status]}</Badge>
              {req.priority === "urgent" && <span className="flex items-center gap-1 text-accent font-semibold text-sm"><Zap className="w-4 h-4" /> عاجل</span>}
            </div>
            <div className="space-y-2 text-sm">
              <Row icon={Wrench} label="الخدمة" value={req.service_type} />
              <Row icon={Car} label="السيارة" value={`${req.vehicle?.make} ${req.vehicle?.model} (${req.vehicle?.year})`} />
              <Row icon={MapPin} label="الموقع" value={req.location} />
              <Row icon={req.drivable ? CheckCircle2 : Zap} label="الحالة" value={req.drivable ? "تتحرك" : "متعطلة بالكامل"} />
            </div>
            <div className="mt-4 p-3 rounded-xl bg-muted/50 text-sm leading-relaxed">{req.description}</div>
          </Card>

          <Card className="p-6">
            <h3 className="font-bold mb-4">مسار الطلب</h3>
            <StatusStepper statuses={REQUEST_STATUSES} current={req.status} />
          </Card>

          {req.selected_quote_id && req.amount_due >= 1 && !req.cancelled && (
            <Card className="p-6 space-y-3" data-testid="repair-pay-card">
              <div className="flex items-center justify-between">
                <span className="font-bold">{req.repair_payments?.length ? "بنود إضافية بانتظار الدفع" : "المبلغ المستحق"}</span>
                <span className="text-xl font-black text-primary" data-testid="amount-due">{Number(req.amount_due).toFixed(2)} ر.س</span>
              </div>
              <p className="text-xs text-muted-foreground">{req.repair_payments?.length ? "بنود وافقت عليها بعد الدفعة السابقة — تُصدر لها فاتورة منفصلة. يظهر سند الاستلام بعد الدفع." : "يشمل عرض الورشة والبنود الإضافية التي وافقت عليها."}</p>
              <PayButtons kind="repair" refId={id} testId="settle-btn" label={req.repair_payments?.length ? "ادفع البنود الإضافية" : "ادفع الآن"} />
            </Card>
          )}

          <RequestInvoices payments={req.repair_payments} onChange={load} />

          {req.final_paid && req.receipt && (
            <Card className="p-6 border-2 border-emerald-500" data-testid="receipt-card">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold flex items-center gap-2"><BadgeCheck className="w-5 h-5 text-emerald-500" /> سند استلام المركبة</h3>
                <Badge className="bg-emerald-500 text-white">مدفوع</Badge>
              </div>
              <div className="space-y-1.5 text-sm">
                <Row icon={Info} label="رقم السند" value={req.receipt.receipt_no} />
                <Row icon={Car} label="المركبة" value={`${req.receipt.vehicle} • ${req.receipt.plate}`} />
                <Row icon={Wrench} label="الصيانة المنفذة" value={req.receipt.service} />
                <Row icon={BadgeCheck} label="الورشة" value={req.receipt.workshop_name} />
                <Row icon={CreditCard} label="المبلغ المدفوع" value={`${req.receipt.amount?.toFixed(0)} ر.س`} />
              </div>
              <div className="mt-4"><WorkPhotos requestId={id} /></div>
              <p className="text-xs text-muted-foreground mt-3">اعرض هذا السند لدى الورشة لاستلام مركبتك.</p>
              <Button onClick={printReceipt} variant="outline" className="w-full rounded-xl mt-3 gap-2" data-testid="print-receipt-btn">
                <Printer className="w-4 h-4" /> طباعة / حفظ PDF
              </Button>
            </Card>
          )}

          {!req.cancelled && !req.repair_payments?.length && req.status !== "closed" && (
            <Button onClick={cancelReq} variant="outline" className="w-full rounded-xl text-destructive border-destructive/30" data-testid="cancel-req-btn">
              إلغاء الطلب
            </Button>
          )}

          {req.selected_quote_id && (
            <Card className="p-6">
              <h3 className="font-bold mb-3">بنود تسعير إضافية</h3>
              <PriceItems requestId={id} role="customer" onChange={load} />
            </Card>
          )}

          {req.selected_quote_id && (
            <Card className="p-6">
              <h3 className="font-bold mb-3">محادثة مع الورشة</h3>
              <ChatBox threadType="request" refId={id} meId={user?.id} />
            </Card>
          )}

          <Card className="p-6">
            <AttachmentsGallery requestId={id} />
          </Card>

          {canRate && (
            <Dialog open={rateOpen} onOpenChange={setRateOpen}>
              <DialogTrigger asChild>
                <Button className="w-full rounded-xl gap-2" data-testid="rate-btn"><Star className="w-4 h-4" /> قيّم الورشة</Button>
              </DialogTrigger>
              <DialogContent dir="rtl">
                <DialogHeader><DialogTitle>تقييم {selectedQuote?.workshop_name}</DialogTitle></DialogHeader>
                <form onSubmit={submitRate} className="space-y-4">
                  <StarInput label="جودة الخدمة" value={rate.service_quality} onChange={(v) => setRate({ ...rate, service_quality: v })} />
                  <StarInput label="سرعة الاستجابة" value={rate.response_speed} onChange={(v) => setRate({ ...rate, response_speed: v })} />
                  <StarInput label="السعر" value={rate.price_rating} onChange={(v) => setRate({ ...rate, price_rating: v })} />
                  <Textarea placeholder="تعليقك (اختياري)" value={rate.comment} onChange={(e) => setRate({ ...rate, comment: e.target.value })} data-testid="rate-comment" />
                  <DialogFooter><Button type="submit" className="w-full rounded-xl" data-testid="rate-submit">إرسال التقييم</Button></DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Middle: quotes */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-lg">عروض الورش ({quotes.length})</h3>
          </div>
          {quotes.length === 0 ? (
            <Card className="p-8 text-center text-muted-foreground text-sm">بانتظار عروض الأسعار من الورش...</Card>
          ) : quotes.map((q, i) => {
            const isSelected = q.id === req.selected_quote_id;
            const disabled = req.selected_quote_id && !isSelected;
            const wp = q.workshop_public || {};
            const revealed = q.revealed;
            return (
              <Card key={q.id} data-testid={`quote-${q.id}`}
                className={`p-5 animate-fade-up ${isSelected ? "border-emerald-500 border-2" : disabled ? "opacity-60" : ""}`}
                style={{ animationDelay: `${i * 60}ms` }}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="font-bold flex items-center gap-2 flex-wrap">
                      {revealed ? <BadgeCheck className="w-4 h-4 text-emerald-500" /> : <ShieldCheck className="w-4 h-4 text-primary" />}
                      {wp.display_name}
                      {revealed && <Badge className="bg-emerald-500 text-white">مختارة</Badge>}
                      {q.status === "rejected" && <Badge variant="outline" className="text-destructive border-destructive/30">مرفوض</Badge>}
                    </div>
                    <div className="flex items-center gap-3 mt-2 flex-wrap">
                      <span className="flex items-center gap-1" title="تقييم عملاء الموقع">
                        <StarDisplay value={wp.rating_avg || 0} size="w-3.5 h-3.5" />
                        <span className="text-xs text-muted-foreground">الموقع ({wp.rating_count || 0})</span>
                      </span>
                      {wp.google_rating > 0 && (
                        <span className="flex items-center gap-1 text-xs" title="تقييم قوقل ماب">
                          <MapPin className="w-3.5 h-3.5 text-primary" />
                          <span className="font-bold">{wp.google_rating}</span>
                          <span className="text-muted-foreground">قوقل ({wp.google_reviews || 0})</span>
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-end shrink-0">
                    <div className="text-2xl font-extrabold font-heading text-primary">{q.total.toFixed(0)}</div>
                    <div className="text-xs text-muted-foreground">ريال</div>
                  </div>
                </div>
                {wp.bio && <p className="text-sm text-muted-foreground mb-3 bg-muted/40 rounded-lg p-2.5 leading-relaxed">{wp.bio}</p>}
                <div className="grid grid-cols-2 gap-2 text-sm text-muted-foreground mb-3">
                  <span>قطع الغيار: {q.parts_price} ر.س</span>
                  <span>أجور العمل: {q.labor_price} ر.س</span>
                  <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {q.duration}</span>
                  {q.extra_fees > 0 && <span>رسوم إضافية: {q.extra_fees} ر.س</span>}
                </div>
                {q.warranty && <div className="flex items-center gap-2 text-sm text-emerald-600 mb-2"><ShieldCheck className="w-4 h-4" /> {q.warranty}</div>}
                {q.notes && <p className="text-sm text-muted-foreground mb-3">{q.notes}</p>}
                {revealed && (
                  <div className="space-y-1.5 text-sm border-t border-border pt-3 mb-1">
                    <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-muted-foreground" /> {wp.phone}</div>
                    {wp.address && <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-muted-foreground" /> {wp.address}</div>}
                    {wp.google_maps_url && <a href={wp.google_maps_url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-primary"><ExternalLink className="w-4 h-4" /> فتح الموقع على الخريطة</a>}
                  </div>
                )}
                {!req.selected_quote_id && (
                  <Button onClick={() => acceptQuote(q.id)} className="w-full rounded-xl" data-testid={`accept-quote-${q.id}`}>قبول هذا العرض</Button>
                )}
              </Card>
            );
          })}
        </div>

        {/* Right: tow */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-lg flex items-center gap-2"><Truck className="w-5 h-5 text-accent" /> سحب المركبة</h3>
          </div>

          {!tow ? (
            <Card className="p-6">
              <p className="text-sm text-muted-foreground mb-4">إذا كانت سيارتك متعطلة أو لا تستطيع قيادتها، اطلب سطحة لنقلها إلى الورشة.</p>
              <Dialog open={towOpen} onOpenChange={setTowOpen}>
                <DialogTrigger asChild>
                  <Button className="w-full rounded-xl gap-2 bg-accent hover:bg-accent/90" data-testid="request-tow-btn"><Truck className="w-4 h-4" /> طلب سحب المركبة</Button>
                </DialogTrigger>
                <DialogContent dir="rtl">
                  <DialogHeader><DialogTitle>طلب سحب المركبة</DialogTitle></DialogHeader>
                  <form onSubmit={createTow} className="space-y-4">
                    <div><Label className="mb-1.5 block">موقع المركبة الحالي</Label><Input value={req.location} disabled /></div>
                    <div><Label className="mb-1.5 block">الوجهة (الورشة)</Label><Input value={tf.to_location} onChange={(e) => setTf({ ...tf, to_location: e.target.value })} required data-testid="tow-to" placeholder="اسم/موقع الورشة" /></div>
                    <div>
                      <Label className="mb-1.5 block">نوع السطحة المطلوبة</Label>
                      <Select value={tf.tow_type} onValueChange={(v) => setTf({ ...tf, tow_type: v })} dir="rtl">
                        <SelectTrigger data-testid="tow-type"><SelectValue /></SelectTrigger>
                        <SelectContent>{TOW_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div><Label className="mb-1.5 block">وقت السحب</Label><Input value={tf.pickup_time} onChange={(e) => setTf({ ...tf, pickup_time: e.target.value })} data-testid="tow-time" /></div>
                    <div><Label className="mb-1.5 block">ملاحظات للسائق</Label><Textarea value={tf.notes} onChange={(e) => setTf({ ...tf, notes: e.target.value })} data-testid="tow-notes" /></div>
                    <DialogFooter><Button type="submit" className="w-full rounded-xl bg-accent hover:bg-accent/90" data-testid="tow-submit">إرسال طلب السطحة</Button></DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </Card>
          ) : (
            <>
              <Card className="p-5">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-bold">حالة السطحة</span>
                  <Badge variant="outline" className={STATUS_COLORS[tow.status]}>{STATUS_LABELS[tow.status]}</Badge>
                </div>
                <div className="text-sm text-muted-foreground">من {tow.from_location} إلى {tow.to_location}</div>
              </Card>

              {!towAccepted ? (
                <>
                  <p className="text-sm font-semibold">عروض السطحات ({towOffers.length})</p>
                  {towOffers.length === 0 ? (
                    <Card className="p-6 text-center text-sm text-muted-foreground">بانتظار عروض مزودي السطحات...</Card>
                  ) : towOffers.map((o) => (
                    <Card key={o.id} className="p-4" data-testid={`tow-offer-${o.id}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="font-bold">{o.provider_name}</div>
                        <div className="text-lg font-extrabold text-accent">{o.price.toFixed(0)} <span className="text-xs text-muted-foreground">ر.س</span></div>
                      </div>
                      <div className="flex items-center gap-3 text-sm text-muted-foreground mb-3">
                        <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {o.eta}</span>
                        <span>{o.tow_type}</span>
                        <StarDisplay value={o.provider_rating || 0} size="w-3.5 h-3.5" />
                      </div>
                      <Button onClick={() => acceptTowOffer(o.id)} className="w-full rounded-xl bg-accent hover:bg-accent/90" data-testid={`accept-tow-${o.id}`}>قبول العرض</Button>
                    </Card>
                  ))}
                </>
              ) : (
                <Card className="p-5">
                  <h4 className="font-bold mb-3">حالة السطحة</h4>
                  <div className="space-y-2 text-sm">
                    <Row icon={Truck} label="السائق" value={towAccepted.driver_name} />
                    <Row icon={Phone} label="الجوال" value={towAccepted.driver_phone} />
                    <Row icon={Clock} label="وقت الوصول" value={towAccepted.eta} />
                  </div>
                  <div className="mt-4">
                    <StatusStepper statuses={TOW_STATUSES} current={tow.status} accent="accent" />
                  </div>
                </Card>
              )}
            </>
          )}
        </div>
      </div>
    </Shell>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-2">
      <Icon className="w-4 h-4 text-muted-foreground shrink-0" />
      <span className="text-muted-foreground">{label}:</span>
      <span className="font-semibold">{value}</span>
    </div>
  );
}
